import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { BOOKING_URL } from "../../lib/links";
import { trackVideo } from "../../lib/video-events";

// The page behind each founding-team email: /for?team=<name>. The GIF in the email links here, and
// the team's own pitch video (rendered by video/pitch, uploaded to the Vercel Blob store pitch-videos
// as pitch/<slug>.mp4, with its opening card as <slug>.jpg) plays at once. Uploading needs no deploy. Muted, because browsers only start a video by itself when it has no sound; it is music only.
// No list of teams lives here, so the public code never names who we have written to. A team whose
// video is missing gets the video for any team. Noindexed at the edge too (see vercel.json).
function useNoIndex() {
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);
}

const PITCH = "https://o4sie09j66qsra66.public.blob.vercel-storage.com/pitch";
const ANY_TEAM = `${PITCH}/teams`;

// Same naming as video/pitch/batch.ts slugFor, so the link finds the rendered file.
const slugFor = (name: string) => name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const CTA_CLASSES =
  "inline-flex items-center justify-center gap-2 rounded-full font-medium transition-all duration-150 ease-out " +
  "bg-brand-600 text-white shadow-sm hover:bg-brand-700 hover:shadow-[0_0_0_4px_rgb(93_199_242_/_0.25)] " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-cyan-400 " +
  "h-12 px-7 text-base";

export default function ForPage() {
  const [searchParams] = useSearchParams();
  const name = (searchParams.get("team") ?? "").trim().replace(/\s+/g, " ").slice(0, 60);
  const slug = slugFor(name);
  const [missing, setMissing] = useState(false);
  const named = slug !== "" && !missing;
  const file = named ? `${PITCH}/${slug}` : ANY_TEAM;

  useNoIndex();

  useEffect(() => {
    trackVideo("for.page_opened", name);
  }, [name]);

  // Each quarter once per page view, so scrubbing back or watching twice doesn't count again.
  const marksSent = useRef(new Set<number>());
  const unmuteSent = useRef(false);
  const reportWatched = (percent: number) => {
    for (const mark of [25, 50, 75, 100]) {
      if (percent >= mark && !marksSent.current.has(mark)) {
        marksSent.current.add(mark);
        trackVideo("for.watched", name, { percent: mark });
      }
    }
  };

  useEffect(() => {
    document.title = named ? `Intakall for ${name}` : "Intakall for your team";
  }, [named, name]);

  return (
    <main className="flex min-h-screen flex-col justify-center bg-white px-6 py-10 sm:py-14">
      <div className="mx-auto flex w-full max-w-4xl flex-col items-center gap-6 text-center">
        <img src="/mark.svg" alt="Intakall" className="h-9 w-9" />

        <h1 className="text-balance font-heading text-3xl font-semibold leading-[1.1] tracking-tight text-ink sm:text-4xl">
          {named ? `Made for the ${name} team` : "Made for teams like yours"}
        </h1>

        <video
          data-testid="for-video"
          key={named ? slug : "any"}
          src={`${file}.mp4`}
          poster={`${file}.jpg`}
          ref={(el) => {
            if (el) el.muted = true;
          }}
          onError={() => setMissing(true)}
          onTimeUpdate={(e) => {
            const { currentTime, duration } = e.currentTarget;
            if (duration > 0) reportWatched((currentTime / duration) * 100);
          }}
          onEnded={() => reportWatched(100)}
          onVolumeChange={(e) => {
            if (!e.currentTarget.muted && !unmuteSent.current) {
              unmuteSent.current = true;
              trackVideo("for.unmuted", name);
            }
          }}
          className="aspect-video w-full rounded-2xl border border-surface-border bg-slate-900 object-cover shadow-[0_0_4px_6px_rgba(44,140,227,0.2),0_0_0_1px_rgba(255,255,255,0.6)_inset]"
          autoPlay
          muted
          playsInline
          controls
          preload="auto"
          aria-label="Intakall: live work context in the call, then the summary, action items and transcript"
        />

        <a
          href={BOOKING_URL}
          target="_blank"
          rel="noopener noreferrer"
          className={CTA_CLASSES}
          onClick={() => trackVideo("for.book_clicked", name)}
        >
          Book 20 minutes
        </a>

        <p className="max-w-xl text-sm leading-relaxed text-slate-500">
          Free for 3 months for founding teams. Twenty minutes to set up, then a short check-in every two weeks.
        </p>
      </div>
    </main>
  );
}
